import { useCallback, useState } from 'react'

import { F0Box, F0Button, F0Link } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { SyncReviewModal } from 'modules/integrations/components/SyncButton/Modals/SyncReviewModal'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useReadSyncIssuesLazyQuery } from 'modules/integrations/hooks/SyncContext/useReadSyncIssuesLazyQuery'
import { usePrivateSyncAction } from 'modules/integrations/hooks/usePrivateSyncAction'
import type { SyncBarStatus } from 'modules/integrations/hooks/useSyncBarData'
import type { SyncFilterDefinition } from 'modules/integrations/lib/syncFilters'

interface SyncBarActionProps {
  syncStatus: SyncBarStatus
  syncFilters?: SyncFilterDefinition[]
  skipConfirmationIfNoIssues?: boolean
  disabled?: boolean
  tooltip?: string
}

export const SyncBarAction = ({
  syncStatus,
  syncFilters = [],
  skipConfirmationIfNoIssues = false,
  disabled = false,
  tooltip,
}: SyncBarActionProps) => {
  const { isSyncing, capabilityMode, isBookkeeperView, runSynchronisation } =
    usePrivateIntegrationsSyncContext()

  const { syncActions, syncModal } = usePrivateSyncAction({
    disabled,
    tooltip,
    syncFilters,
    skipConfirmationIfNoIssues,
  })

  const syncAction = syncActions[0]
  const isExportMode = capabilityMode === 'file'
  const hasIssues = syncStatus === 'failed'

  const [showReviewModal, setShowReviewModal] = useState(false)

  const { syncResultIssues, loading: issuesLoading } = useReadSyncIssuesLazyQuery({
    skip: isSyncing || !hasIssues || !showReviewModal,
  })

  const handleReviewIssuesClick = useCallback(() => {
    setShowReviewModal(true)
  }, [])

  const handleReviewClose = useCallback(() => {
    setShowReviewModal(false)
  }, [])

  const handleReviewSubmit = useCallback(async () => {
    setShowReviewModal(false)
    await runSynchronisation()
  }, [runSynchronisation])

  if (!syncAction) return null

  return (
    <>
      <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
        {isBookkeeperView && hasIssues && (
          <F0Box marginRight="lg">
            <F0Link
              variant="link"
              onClick={handleReviewIssuesClick}
              disabled={syncAction.disabled || undefined}
            >
              {i18n.t('integrations.sync_bar.action.review')}
            </F0Link>
          </F0Box>
        )}
        <F0Button
          variant="outline"
          label={syncAction.label}
          onClick={syncAction.onClick}
          disabled={syncAction.disabled || undefined}
          loading={syncAction.loading || undefined}
          tooltip={
            syncAction.disabled && tooltip
              ? tooltip
              : syncAction.tooltip?.({
                  disabled: syncAction.disabled ?? false,
                  loading: syncAction.loading ?? false,
                })
          }
          icon={syncAction.icon}
        />
      </F0Box>
      {syncModal}
      {showReviewModal && (
        <SyncReviewModal
          isLoading={issuesLoading}
          onClose={handleReviewClose}
          onSubmit={handleReviewSubmit}
          issues={syncResultIssues}
          anyValidSyncable={false}
          title={i18n.t(
            'bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.modal_title'
          )}
          description={i18n.t(
            'bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.modal_description'
          )}
          submitLabel={
            isExportMode
              ? i18n.t('integrations.confirmation_modal.export_anyway_cta')
              : i18n.t('integrations.confirmation_modal.sync_anyway_cta')
          }
        />
      )}
    </>
  )
}
